import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  TextInput,
  Modal,
  Pressable,
  Alert,
  Image,
  FlatList,
  Animated,
  Linking,
  StatusBar,
  Dimensions,
  RefreshControl,
  ActivityIndicator,
} from 'react-native';
import {
  Ionicons,
  MaterialIcons,
  MaterialCommunityIcons,
  FontAwesome5,
  AntDesign,
} from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';
import useProfile from '../../hooks/useProfile';
// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const { width, height } = Dimensions.get('window');


// JOBENG Logo component
const JobengLogo = () => (
  <Image
    source={require('../../../assets/jobeng_icon.png')}
    style={{ width: 30, height: 30 }}
    resizeMode="contain"
  />
);

export default function SupportScreen({ navigation }) {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // Main states
  const [activeTab, setActiveTab] = useState('help');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [portfolioData, setPortfolioData] = useState(null);
  
  // Modal states
  const [showChatModal, setShowChatModal] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showSystemStatusModal, setShowSystemStatusModal] = useState(false);
  
  // UI states
  const [expandedFAQs, setExpandedFAQs] = useState([]);
  const [chatTyping, setChatTyping] = useState(false);
  const [ticketSubmitting, setTicketSubmitting] = useState(false);
  
  // Chat states
  const [chatMessages, setChatMessages] = useState([
    {
      id: 1,
      text: 'Hello! I\'m your JOBENG AI assistant. How can I help you today? 🤖',
      isUser: false,
      timestamp: new Date(),
      type: 'greeting',
    },
  ]);
  const [newMessage, setNewMessage] = useState('');
  
  // Form states
  const [ticketForm, setTicketForm] = useState({
    subject: '',
    category: 'General',
    description: '',
    email: '',
    priority: 'Medium',
    attachments: [],
  });
  
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    department: 'General Support',
  });
  
  const [feedbackForm, setFeedbackForm] = useState({
    rating: 0,
    category: 'App Experience',
    feedback: '',
    suggestions: '',
    email: '',
  });

  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;
  const scaleAnim = useRef(new Animated.Value(0.9)).current;

  // Refs
  const scrollViewRef = useRef();
  const chatScrollRef = useRef();

  // Load data on component mount and focus
  useEffect(() => {
    initializeScreen();
  }, []);

  useFocusEffect(
    React.useCallback(() => {
      loadPortfolioData();
    }, [])
  );

  const initializeScreen = async () => {
    try {
      setLoading(true);
      
      // Load portfolio data
      await loadPortfolioData();
      
      // Start animations
      startAnimations();
      
    } catch (error) {
      console.error('❌ Error initializing support screen:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPortfolioData = async () => {
    try {
      console.log('🔄 Loading portfolio data for support...');
      
      // Try to load from AsyncStorage
      const profileData = await AsyncStorage.getItem('userProfile');
      const skillsData = await AsyncStorage.getItem('userSkills');
      const projectsData = await AsyncStorage.getItem('userProjects');
      
      const portfolio = {
        profile: profileData ? JSON.parse(profileData) : {},
        skills: skillsData ? JSON.parse(skillsData) : [],
        projects: projectsData ? JSON.parse(projectsData) : [],
      };

      setPortfolioData(portfolio);
      console.log('✅ Portfolio data loaded for support');
      
    } catch (error) {
      console.error('❌ Error loading portfolio data:', error);
    }
  };

  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await loadPortfolioData();
    } catch (error) {
      console.error('❌ Error refreshing:', error);
    } finally {
      setRefreshing(false);
    }
  };

  // ** ENHANCED NAVIGATION FUNCTIONS **
  const navigateToScreen = (screenName, params = {}) => {
    try {
      console.log(`🧭 Navigating to ${screenName} from Support`);
      
      // Enhanced navigation with multiple attempts
      const possibleScreenNames = [
        screenName,
        `${screenName}Screen`,
        screenName.replace('Screen', ''),
      ];

      let navigationSuccessful = false;

      for (const name of possibleScreenNames) {
        try {
          navigation.navigate(name, {
            ...params,
            source: 'SupportScreen',
            timestamp: new Date().toISOString()
          });
          navigationSuccessful = true;
          console.log(`✅ Successfully navigated to ${name}`);
          break;
        } catch (navError) {
          console.log(`⚠️ Failed to navigate to ${name}, trying next option...`);
        }
      }

      if (!navigationSuccessful) {
        console.error(`❌ Failed to navigate to any variant of ${screenName}`);
        Alert.alert(
          'Navigation Error',
          `Unable to navigate to ${screenName}. Please try again or contact support.`,
          [{ text: 'OK' }]
        );
      }

    } catch (error) {
      console.error(`❌ Navigation error for ${screenName}:`, error);
      Alert.alert('Navigation Error', 'Unable to navigate to the requested screen.');
    }
  };

  // Quick navigation functions for specific screens
  const navigateToSettings = () => navigateToScreen('Settings');
  const navigateToPayments = () => navigateToScreen('Payments');
  const navigateToMyLearningHistoryScreen = () => navigateToScreen('MyLearningHistoryScreen');
  const navigateToBuildResume = () => navigateToScreen('BuildResume');
  const navigateToJobApplications = () => navigateToScreen('JobApplications');
  const navigateToProfile = () => navigateToScreen('Profile');
  const navigateToPrivacySettings = () => navigateToScreen('PrivacySettings');
  const navigateToAccountSettings = () => navigateToScreen('AccountSettings');

  // Enhanced AI chatbot with navigation suggestions
  const simulateEnhancedChatbotResponse = (userMessage) => {
    setChatTyping(true);
    
    const lowerCaseMessage = userMessage.toLowerCase();
    let botResponse = "I'm here to help! Could you please provide more details about your question?";
    let responseType = 'general';
    let suggestions = [];
    let navigationAction = null;

    // Navigation-aware responses
    if (lowerCaseMessage.includes('settings') || lowerCaseMessage.includes('setting')) {
      botResponse = 'I can help you with settings! You can access your general settings, account settings, or privacy settings. Which would you like to navigate to?';
      responseType = 'navigation';
      suggestions = ['General Settings', 'Account Settings', 'Privacy Settings', 'Notification Settings'];
      navigationAction = 'settings';
    } else if (lowerCaseMessage.includes('payment') || lowerCaseMessage.includes('billing') || lowerCaseMessage.includes('subscription')) {
      botResponse = 'For payment and billing matters, I can take you directly to the Payments section where you can manage your subscription, view billing history, and update payment methods.';
      responseType = 'navigation';
      suggestions = ['Go to Payments', 'View Billing', 'Manage Subscription', 'Payment Methods'];
      navigationAction = 'payments';
    } else if (lowerCaseMessage.includes('learn') || lowerCaseMessage.includes('course') || lowerCaseMessage.includes('training')) {
      botResponse = 'Great! I can direct you to the Learning section where you can browse courses, track your progress, and access certifications. Would you like me to take you there?';
      responseType = 'navigation';
      suggestions = ['Go to Learning', 'Browse Courses', 'My Progress', 'Certifications'];
      navigationAction = 'learning';
    } else if (lowerCaseMessage.includes('resume') || lowerCaseMessage.includes('cv')) {
      botResponse = 'I can help you with your resume! Our AI Resume Builder can help you create, edit, and optimize your resume. Shall I take you to the Resume Builder?';
      responseType = 'navigation';
      suggestions = ['Go to Resume Builder', 'Create Resume', 'Edit Resume', 'Resume Tips'];
      navigationAction = 'resume';
    } else if (lowerCaseMessage.includes('application') || lowerCaseMessage.includes('job') && lowerCaseMessage.includes('track')) {
      botResponse = 'You can track all your job applications in the Job Applications section. I can take you there to view your application status, upcoming interviews, and more.';
      responseType = 'navigation';
      suggestions = ['Go to Applications', 'Track Applications', 'Interview Schedule', 'Application Status'];
      navigationAction = 'applications';
    } else if (lowerCaseMessage.includes('profile') || lowerCaseMessage.includes('portfolio')) {
      botResponse = 'Your profile is the heart of your JOBENG experience! I can take you to your Profile section where you can update your information, manage your portfolio, and enhance your visibility to employers.';
      responseType = 'navigation';
      suggestions = ['Go to Profile', 'Edit Profile', 'Portfolio Management', 'Profile Visibility'];
      navigationAction = 'profile';
    } else if (lowerCaseMessage.includes('privacy') || lowerCaseMessage.includes('data')) {
      botResponse = 'Privacy is important! I can take you to Privacy Settings where you can control what information is shared, manage data preferences, and review privacy options.';
      responseType = 'navigation';
      suggestions = ['Go to Privacy Settings', 'Data Control', 'Sharing Preferences', 'Privacy Options'];
      navigationAction = 'privacy';
    } else if (lowerCaseMessage.includes('account')) {
      botResponse = 'For account-related settings like password, email, notifications, and security, I can direct you to Account Settings. Would you like me to take you there?';
      responseType = 'navigation';
      suggestions = ['Go to Account Settings', 'Change Password', 'Update Email', 'Security Settings'];
      navigationAction = 'account';
    }

    // General helpful responses
    if (responseType === 'general') {
      if (lowerCaseMessage.includes('hello') || lowerCaseMessage.includes('hi')) {
        botResponse = 'Hello! I\'m your JOBENG AI assistant. I can help you navigate to different sections of the app, answer questions, or provide support. What would you like help with today?';
        suggestions = ['Settings', 'Payments', 'Learning', 'Resume Builder', 'Job Applications', 'Profile'];
      } else if (lowerCaseMessage.includes('help') || lowerCaseMessage.includes('support')) {
        botResponse = 'I\'m here to help! I can assist you with navigating the app, answer questions about features, or help you find what you\'re looking for. You can also access different sections directly through our navigation options.';
        suggestions = ['App Navigation', 'Feature Questions', 'Technical Support', 'Account Help'];
      } else if (lowerCaseMessage.includes('navigate') || lowerCaseMessage.includes('go to') || lowerCaseMessage.includes('find')) {
        botResponse = 'I can help you navigate to any section of the app! Here are the main areas you can access:';
        suggestions = ['Settings', 'Payments', 'Learning', 'Resume Builder', 'Job Applications', 'Profile', 'Privacy Settings', 'Account Settings'];
      }
    }

    // Add bot response after a delay to simulate thinking
    setTimeout(() => {
      setChatTyping(false);
      setChatMessages(prevMessages => [
        ...prevMessages,
        {
          id: prevMessages.length + 2,
          text: botResponse,
          isUser: false,
          timestamp: new Date(),
          type: responseType,
          suggestions: suggestions,
          navigationAction: navigationAction,
        },
      ]);
    }, 1500);
  };

  // Handle sending a chat message
  const handleSendMessage = () => {
    if (newMessage.trim() === '') return;

    // Add user message
    setChatMessages(prevMessages => [
      ...prevMessages,
      {
        id: prevMessages.length + 1,
        text: newMessage,
        isUser: true,
        timestamp: new Date(),
      },
    ]);

    // Get enhanced bot response
    simulateEnhancedChatbotResponse(newMessage);

    // Clear input
    setNewMessage('');
    
    // Scroll to bottom
    setTimeout(() => {
      chatScrollRef.current?.scrollToEnd({ animated: true });
    }, 100);
  };

  // Handle chat suggestion tap with navigation
  const handleSuggestionTap = (suggestion, navigationAction) => {
    if (navigationAction) {
      // Handle navigation suggestions
      switch (navigationAction) {
        case 'settings':
          if (suggestion === 'General Settings') navigateToSettings();
          else if (suggestion === 'Account Settings') navigateToAccountSettings();
          else if (suggestion === 'Privacy Settings') navigateToPrivacySettings();
          else navigateToSettings();
          break;
        case 'payments':
          navigateToPayments();
          break;
        case 'learning':
          navigateToMyLearningHistoryScreen();
          break;
        case 'resume':
          navigateToBuildResume();
          break;
        case 'applications':
          navigateToJobApplications();
          break;
        case 'profile':
          navigateToProfile();
          break;
        case 'privacy':
          navigateToPrivacySettings();
          break;
        case 'account':
          navigateToAccountSettings();
          break;
        default:
          // Handle direct navigation suggestions
          if (suggestion === 'Settings') navigateToSettings();
          else if (suggestion === 'Payments') navigateToPayments();
          else if (suggestion === 'Learning') navigateToMyLearningHistoryScreen();
          else if (suggestion === 'Resume Builder') navigateToBuildResume();
          else if (suggestion === 'Job Applications') navigateToJobApplications();
          else if (suggestion === 'Profile') navigateToProfile();
          else if (suggestion === 'Privacy Settings') navigateToPrivacySettings();
          else if (suggestion === 'Account Settings') navigateToAccountSettings();
          break;
      }
    } else {
      // Regular chat suggestion
      setNewMessage(suggestion);
      handleSendMessage();
    }
  };

  // Quick action functions
  const handleQuickAction = (action) => {
    console.log(`🎯 Quick action: ${action}`);
    
    switch (action) {
      case 'ai_assistant':
        setShowChatModal(true);
        break;
      case 'support_ticket':
        setShowTicketModal(true);
        break;
      case 'system_status':
        setShowSystemStatusModal(true);
        break;
      case 'feedback':
        setShowFeedbackModal(true);
        break;
      case 'settings':
        navigateToSettings();
        break;
      case 'payments':
        navigateToPayments();
        break;
      case 'learning':
        navigateToMyLearningHistoryScreen();
        break;
      case 'resume_builder':
        navigateToBuildResume();
        break;
      case 'job_applications':
        navigateToJobApplications();
        break;
      case 'profile':
        navigateToProfile();
        break;
      case 'privacy_settings':
        navigateToPrivacySettings();
        break;
      case 'account_settings':
        navigateToAccountSettings();
        break;
      default:
        console.log('Unknown quick action:', action);
    }
  };

  // Handle submitting a support ticket
  const handleSubmitTicket = async () => {
    // Validate form
    if (!ticketForm.subject || !ticketForm.description || !ticketForm.email) {
      Alert.alert('Missing Information', 'Please fill in all required fields.');
      return;
    }

    setTicketSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Generate ticket ID
      const ticketId = `JOBENG-${Date.now().toString().slice(-6)}`;
      
      Alert.alert(
        'Ticket Submitted Successfully! 🎫',
        `Your support ticket #${ticketId} has been submitted. Our team will respond within 24 hours via email.`,
        [
          {
            text: 'OK',
            onPress: () => {
              setShowTicketModal(false);
              setTicketForm({
                subject: '',
                category: 'General',
                description: '',
                email: '',
                priority: 'Medium',
                attachments: [],
              });
            },
          },
        ]
      );
    } catch (error) {
      Alert.alert('Error', 'Failed to submit ticket. Please try again.');
    } finally {
      setTicketSubmitting(false);
    }
  };

  // Handle submitting the contact form
  const handleSubmitContactForm = async () => {
    // Validate form
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      Alert.alert('Missing Information', 'Please fill in all required fields.');
      return;
    }

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      Alert.alert(
        'Message Sent Successfully! 📧',
        'Your message has been sent to our support team. We will respond to you via email within 24 hours.',
        [
          {
            text: 'OK',
            onPress: () => {
              setContactForm({
                name: '',
                email: '',
                subject: '',
                message: '',
                department: 'General Support',
              });
            },
          },
        ]
      );
    } catch (error) {
      Alert.alert('Error', 'Failed to send message. Please try again.');
    }
  };

  // Handle submitting feedback
  const handleSubmitFeedback = async () => {
    if (feedbackForm.rating === 0 || !feedbackForm.feedback) {
      Alert.alert('Missing Information', 'Please provide a rating and feedback.');
      return;
    }

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      Alert.alert(
        'Thank You for Your Feedback! 🌟',
        'Your feedback helps us improve JOBENG. We appreciate you taking the time to share your thoughts.',
        [
          {
            text: 'OK',
            onPress: () => {
              setShowFeedbackModal(false);
              setFeedbackForm({
                rating: 0,
                category: 'App Experience',
                feedback: '',
                suggestions: '',
                email: '',
              });
            },
          },
        ]
      );
    } catch (error) {
      Alert.alert('Error', 'Failed to submit feedback. Please try again.');
    }
  };

  // Toggle FAQ expansion
  const toggleFAQ = (id) => {
    if (expandedFAQs.includes(id)) {
      setExpandedFAQs(expandedFAQs.filter(faqId => faqId !== id));
    } else {
      setExpandedFAQs([...expandedFAQs, id]);
    }
  };

  // Enhanced FAQs with navigation context
  const getEnhancedFAQs = () => {
    return [
      {
        id: 1,
        category: 'Account',
        question: 'How do I update my account settings?',
        answer: 'You can update your account settings by going to Settings > Account Settings. There you can change your password, update your email, manage notifications, and configure security options.',
        tags: ['account', 'settings', 'password'],
        navigationAction: () => navigateToAccountSettings(),
        navigationText: 'Go to Account Settings',
      },
      {
        id: 2,
        category: 'Privacy',
        question: 'How do I control my privacy settings?',
        answer: 'Your privacy is important to us. You can manage all privacy settings in Settings > Privacy Settings. Control what information is shared with employers, manage data preferences, and review privacy options.',
        tags: ['privacy', 'data', 'settings'],
        navigationAction: () => navigateToPrivacySettings(),
        navigationText: 'Go to Privacy Settings',
      },
      {
        id: 3,
        category: 'Payments',
        question: 'How do I manage my subscription and payments?',
        answer: 'You can manage your subscription, view billing history, and update payment methods in the Payments section. Access it through Settings > Payments or directly from the main menu.',
        tags: ['payments', 'billing', 'subscription'],
        navigationAction: () => navigateToPayments(),
        navigationText: 'Go to Payments',
      },
      {
        id: 4,
        category: 'Learning',
        question: 'Where can I find my courses and certifications?',
        answer: 'All your learning materials, course progress, and certifications are available in the Learning section. You can browse new courses, track your progress, and download certificates.',
        tags: ['learning', 'courses', 'certifications'],
        navigationAction: () => navigateToMyLearningHistoryScreen(),
        navigationText: 'Go to Learning',
      },
      {
        id: 5,
        category: 'Resume',
        question: 'How do I create or edit my resume?',
        answer: 'Use our AI-powered Resume Builder to create professional resumes. You can access it from the main menu or through your Profile section. The AI helps optimize your resume for ATS systems and specific job roles.',
        tags: ['resume', 'cv', 'builder'],
        navigationAction: () => navigateToBuildResume(),
        navigationText: 'Go to Resume Builder',
      },
      {
        id: 6,
        category: 'Applications',
        question: 'How do I track my job applications?',
        answer: 'Track all your job applications, interview schedules, and application status in the Job Applications section. Get real-time updates on your application progress and manage your job search efficiently.',
        tags: ['applications', 'jobs', 'tracking'],
        navigationAction: () => navigateToJobApplications(),
        navigationText: 'Go to Job Applications',
      },
      {
        id: 7,
        category: 'Profile',
        question: 'How do I update my profile and portfolio?',
        answer: 'Your profile is your professional showcase. Update your information, manage your portfolio, add projects and skills, and control your visibility to employers in the Profile section.',
        tags: ['profile', 'portfolio', 'update'],
        navigationAction: () => navigateToProfile(),
        navigationText: 'Go to Profile',
      },
      {
        id: 8,
        category: 'Settings',
        question: 'Where can I find all app settings?',
        answer: 'All app settings are organized in the Settings section. You can access account settings, privacy settings, notification preferences, and general app configurations from there.',
        tags: ['settings', 'configuration', 'preferences'],
        navigationAction: () => navigateToSettings(),
        navigationText: 'Go to Settings',
      },
    ];
  };

  // Quick navigation cards data
  const getQuickNavigationCards = () => {
    return [
      {
        id: 'settings',
        title: 'Settings',
        description: 'App preferences and configurations',
        icon: 'settings-outline',
        color: '#0e4e96',
        action: () => navigateToSettings(),
      },
      {
        id: 'payments',
        title: 'Payments',
        description: 'Billing and subscription management',
        icon: 'card-outline',
        color: '#10B981',
        action: () => navigateToPayments(),
      },
      {
        id: 'learning',
        title: 'Learning',
        description: 'Courses and certifications',
        icon: 'school-outline',
        color: '#8B5CF6',
        action: () => navigateToMyLearningHistoryScreen(),
      },
      {
        id: 'resume_builder',
        title: 'Resume Builder',
        description: 'AI-powered resume creation',
        icon: 'document-text-outline',
        color: '#F59E0B',
        action: () => navigateToBuildResume(),
      },
      {
        id: 'job_applications',
        title: 'Job Applications',
        description: 'Track your applications',
        icon: 'briefcase-outline',
        color: '#EF4444',
        action: () => navigateToJobApplications(),
      },
      {
        id: 'profile',
        title: 'Profile',
        description: 'Your professional showcase',
        icon: 'person-outline',
        color: '#06B6D4',
        action: () => navigateToProfile(),
      },
      {
        id: 'privacy_settings',
        title: 'Privacy Settings',
        description: 'Control your data and privacy',
        icon: 'shield-checkmark-outline',
        color: '#84CC16',
        action: () => navigateToPrivacySettings(),
      },
      {
        id: 'account_settings',
        title: 'Account Settings',
        description: 'Manage your account details',
        icon: 'person-circle-outline',
        color: '#F97316',
        action: () => navigateToAccountSettings(),
      },
    ];
  };

  // System status data
  const systemStatus = {
    overall: 'operational',
    services: [
      { name: 'Job Search', status: 'operational', uptime: '99.9%' },
      { name: 'Portfolio System', status: 'operational', uptime: '99.8%' },
      { name: 'AI Resume Builder', status: 'operational', uptime: '99.7%' },
      { name: 'Learning Platform', status: 'operational', uptime: '99.9%' },
      { name: 'Notifications', status: 'maintenance', uptime: '98.5%' },
      { name: 'Payment System', status: 'operational', uptime: '99.6%' },
    ],
    lastUpdated: new Date().toLocaleString(),
  };

  // Render loading state
  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
        
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Ionicons name="arrow-back" size={24} color="#ffffff" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Support</Text>
          <JobengLogo />
        </View>

        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading support center...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color="#ffffff" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Support Center</Text>
        <JobengLogo />
      </View>

      {/* Tab Navigation */}
      <View style={styles.tabContainer}>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'help' && styles.activeTab]}
          onPress={() => setActiveTab('help')}
        >
          <Ionicons 
            name="help-circle-outline" 
            size={20} 
            color={activeTab === 'help' ? '#ffffff' : '#64748B'} 
          />
          <Text style={[
            styles.tabText,
            activeTab === 'help' && styles.activeTabText
          ]}>
            Help Center
          </Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={[styles.tab, activeTab === 'navigation' && styles.activeTab]}
          onPress={() => setActiveTab('navigation')}
        >
          <Ionicons 
            name="compass-outline" 
            size={20} 
            color={activeTab === 'navigation' ? '#ffffff' : '#64748B'} 
          />
          <Text style={[
            styles.tabText,
            activeTab === 'navigation' && styles.activeTabText
          ]}>
            Quick Access
          </Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={[styles.tab, activeTab === 'contact' && styles.activeTab]}
          onPress={() => setActiveTab('contact')}
        >
          <Ionicons 
            name="mail-outline" 
            size={20} 
            color={activeTab === 'contact' ? '#ffffff' : '#64748B'} 
          />
          <Text style={[
            styles.tabText,
            activeTab === 'contact' && styles.activeTabText
          ]}>
            Contact Us
          </Text>
        </TouchableOpacity>
      </View>

      <ScrollView
        ref={scrollViewRef}
        style={styles.container}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={['#0e4e96']}
            tintColor="#0e4e96"
          />
        }
      >
        {/* Help Center Tab */}
        {activeTab === 'help' && (
          <Animated.View
            style={[
              styles.tabContent,
              {
                opacity: fadeAnim,
                transform: [{ translateY: slideAnim }]
              }
            ]}
          >
            {/* Quick Actions */}
            <View style={styles.quickActionsContainer}>
              <Text style={styles.sectionTitle}>Quick Actions</Text>
              <View style={styles.quickActionsGrid}>
                <TouchableOpacity
                  style={styles.quickActionCard}
                  onPress={() => handleQuickAction('ai_assistant')}
                >
                  <View style={[styles.quickActionIcon, { backgroundColor: '#0e4e96' }]}>
                    <MaterialIcons name="smart-toy" size={24} color="#ffffff" />
                  </View>
                  <Text style={styles.quickActionTitle}>AI Assistant</Text>
                  <Text style={styles.quickActionDescription}>Get instant help</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.quickActionCard}
                  onPress={() => handleQuickAction('support_ticket')}
                >
                  <View style={[styles.quickActionIcon, { backgroundColor: '#10B981' }]}>
                    <MaterialIcons name="support-agent" size={24} color="#ffffff" />
                  </View>
                  <Text style={styles.quickActionTitle}>Support Ticket</Text>
                  <Text style={styles.quickActionDescription}>Report an issue</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.quickActionCard}
                  onPress={() => handleQuickAction('system_status')}
                >
                  <View style={[styles.quickActionIcon, { backgroundColor: '#8B5CF6' }]}>
                    <MaterialIcons name="health-and-safety" size={24} color="#ffffff" />
                  </View>
                  <Text style={styles.quickActionTitle}>System Status</Text>
                  <Text style={styles.quickActionDescription}>Check service health</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.quickActionCard}
                  onPress={() => handleQuickAction('feedback')}
                >
                  <View style={[styles.quickActionIcon, { backgroundColor: '#F59E0B' }]}>
                    <MaterialIcons name="feedback" size={24} color="#ffffff" />
                  </View>
                  <Text style={styles.quickActionTitle}>Feedback</Text>
                  <Text style={styles.quickActionDescription}>Share your thoughts</Text>
                </TouchableOpacity>
              </View>
            </View>

            {/* Search FAQs */}
            <View style={styles.searchContainer}>
              <Text style={styles.sectionTitle}>Frequently Asked Questions</Text>
              <View style={styles.searchInputContainer}>
                <Ionicons name="search" size={20} color="#64748B" />
                <TextInput
                  style={styles.searchInput}
                  placeholder="Search FAQs..."
                  placeholderTextColor="#94A3B8"
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                />
              </View>
            </View>

            {/* FAQs List */}
            <View style={styles.faqsContainer}>
              {getEnhancedFAQs()
                .filter(faq => 
                  searchQuery === '' || 
                  String(faq.question ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                  String(faq.answer ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                  faq.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
                )
                .map((faq) => (
                  <View key={faq.id} style={styles.faqCard}>
                    <TouchableOpacity
                      style={styles.faqHeader}
                      onPress={() => toggleFAQ(faq.id)}
                    >
                      <View style={styles.faqTitleContainer}>
                        <Text style={styles.faqCategory}>{faq.category}</Text>
                        <Text style={styles.faqQuestion}>{faq.question}</Text>
                      </View>
                      <Ionicons
                        name={expandedFAQs.includes(faq.id) ? 'chevron-up' : 'chevron-down'}
                        size={20}
                        color="#64748B"
                      />
                    </TouchableOpacity>
                    
                    {expandedFAQs.includes(faq.id) && (
                      <View style={styles.faqContent}>
                        <Text style={styles.faqAnswer}>{faq.answer}</Text>
                        {faq.navigationAction && (
                          <TouchableOpacity
                            style={styles.faqNavigationButton}
                            onPress={faq.navigationAction}
                          >
                            <MaterialIcons name="arrow-forward" size={16} color="#0e4e96" />
                            <Text style={styles.faqNavigationText}>{faq.navigationText}</Text>
                          </TouchableOpacity>
                        )}
                      </View>
                    )}
                  </View>
                ))}
            </View>
          </Animated.View>
        )}

        {/* Quick Access Tab */}
        {activeTab === 'navigation' && (
          <Animated.View
            style={[
              styles.tabContent,
              {
                opacity: fadeAnim,
                transform: [{ translateY: slideAnim }]
              }
            ]}
          >
            <View style={styles.navigationContainer}>
              <Text style={styles.sectionTitle}>Quick Navigation</Text>
              <Text style={styles.sectionDescription}>
                Access different sections of the app quickly
              </Text>
              
              <View style={styles.navigationGrid}>
                {getQuickNavigationCards().map((card) => (
                  <TouchableOpacity
                    key={card.id}
                    style={styles.navigationCard}
                    onPress={card.action}
                  >
                    <View style={[styles.navigationIcon, { backgroundColor: card.color }]}>
                      <Ionicons name={card.icon} size={24} color="#ffffff" />
                    </View>
                    <Text style={styles.navigationTitle}>{card.title}</Text>
                    <Text style={styles.navigationDescription}>{card.description}</Text>
                    <View style={styles.navigationArrow}>
                      <Ionicons name="chevron-forward" size={16} color="#64748B" />
                    </View>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </Animated.View>
        )}

        {/* Contact Us Tab */}
        {activeTab === 'contact' && (
          <Animated.View
            style={[
              styles.tabContent,
              {
                opacity: fadeAnim,
                transform: [{ translateY: slideAnim }]
              }
            ]}
          >
            {/* Contact Information */}
            <View style={styles.contactInfoContainer}>
              <Text style={styles.sectionTitle}>Get in Touch</Text>
              <Text style={styles.sectionDescription}>
                We're here to help! Choose the best way to reach us.
              </Text>

              <View style={styles.contactMethodsGrid}>
                <View style={styles.contactMethod}>
                  <View style={[styles.contactIcon, { backgroundColor: '#0e4e96' }]}>
                    <MaterialIcons name="email" size={24} color="#ffffff" />
                  </View>
                  <Text style={styles.contactMethodTitle}>Email Support</Text>
                  <Text style={styles.contactMethodText}>support@jobeng.uk</Text>
                  <Text style={styles.contactMethodSubtext}>Response within 24 hours</Text>
                </View>

                <View style={styles.contactMethod}>
                  <View style={[styles.contactIcon, { backgroundColor: '#10B981' }]}>
                    <MaterialIcons name="phone" size={24} color="#ffffff" />
                  </View>
                  <Text style={styles.contactMethodTitle}>Phone Support</Text>
                  <Text style={styles.contactMethodText}>+44 20 1234 5678</Text>
                  <Text style={styles.contactMethodSubtext}>Mon-Fri, 9AM-6PM GMT</Text>
                </View>

                <View style={styles.contactMethod}>
                  <View style={[styles.contactIcon, { backgroundColor: '#8B5CF6' }]}>
                    <MaterialIcons name="chat" size={24} color="#ffffff" />
                  </View>
                  <Text style={styles.contactMethodTitle}>Live Chat</Text>
                  <Text style={styles.contactMethodText}>Available 24/7</Text>
                  <Text style={styles.contactMethodSubtext}>Instant responses</Text>
                </View>
              </View>
            </View>

            {/* Contact Form */}
            <View style={styles.contactFormContainer}>
              <Text style={styles.formTitle}>Send us a Message</Text>
              
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Name *</Text>
                <TextInput
                  style={styles.textInput}
                  value={contactForm.name}
                  onChangeText={(text) => setContactForm({...contactForm, name: text})}
                  placeholder="Your full name"
                  placeholderTextColor="#94A3B8"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Email *</Text>
                <TextInput
                  style={styles.textInput}
                  value={contactForm.email}
                  onChangeText={(text) => setContactForm({...contactForm, email: text})}
                  placeholder="your.email@example.com"
                  placeholderTextColor="#94A3B8"
                  keyboardType="email-address"
                  autoCapitalize="none"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Subject</Text>
                <TextInput
                  style={styles.textInput}
                  value={contactForm.subject}
                  onChangeText={(text) => setContactForm({...contactForm, subject: text})}
                  placeholder="Brief description of your inquiry"
                  placeholderTextColor="#94A3B8"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Message *</Text>
                <TextInput
                  style={[styles.textInput, styles.textArea]}
                  value={contactForm.message}
                  onChangeText={(text) => setContactForm({...contactForm, message: text})}
                  placeholder="Please describe your question or issue in detail..."
                  placeholderTextColor="#94A3B8"
                  multiline
                  numberOfLines={4}
                  textAlignVertical="top"
                />
              </View>

              <TouchableOpacity
                style={styles.submitButton}
                onPress={handleSubmitContactForm}
              >
                <MaterialIcons name="send" size={20} color="#ffffff" />
                <Text style={styles.submitButtonText}>Send Message</Text>
              </TouchableOpacity>
            </View>
          </Animated.View>
        )}
      </ScrollView>

      {/* AI Chat Modal */}
      <Modal
        visible={showChatModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowChatModal(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.chatModalContent}>
            <View style={styles.chatHeader}>
              <View style={styles.chatHeaderInfo}>
                <View style={styles.aiAvatar}>
                  <MaterialIcons name="smart-toy" size={20} color="#ffffff" />
                </View>
                <View>
                  <Text style={styles.chatHeaderTitle}>JOBENG AI Assistant</Text>
                  <Text style={styles.chatHeaderSubtitle}>Here to help you navigate</Text>
                </View>
              </View>
              <TouchableOpacity onPress={() => setShowChatModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>

            <ScrollView
              ref={chatScrollRef}
              style={styles.chatMessages}
              showsVerticalScrollIndicator={false}
            >
              {chatMessages.map((message) => (
                <View key={message.id} style={styles.messageContainer}>
                  <View style={[
                    styles.messageBubble,
                    message.isUser ? styles.userMessage : styles.botMessage
                  ]}>
                    <Text style={[
                      styles.messageText,
                      message.isUser ? styles.userMessageText : styles.botMessageText
                    ]}>
                      {message.text}
                    </Text>
                  </View>
                  
                  {!message.isUser && message.suggestions && (
                    <View style={styles.suggestionsContainer}>
                      {message.suggestions.map((suggestion, index) => (
                        <TouchableOpacity
                          key={index}
                          style={styles.suggestionButton}
                          onPress={() => handleSuggestionTap(suggestion, message.navigationAction)}
                        >
                          <Text style={styles.suggestionText}>{suggestion}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  )}
                </View>
              ))}
              
              {chatTyping && (
                <View style={styles.typingIndicator}>
                  <View style={styles.typingBubble}>
                    <Text style={styles.typingText}>AI is typing...</Text>
                  </View>
                </View>
              )}
            </ScrollView>

            <View style={styles.chatInputContainer}>
              <TextInput
                style={styles.chatInput}
                value={newMessage}
                onChangeText={setNewMessage}
                placeholder="Ask me anything..."
                placeholderTextColor="#94A3B8"
                multiline
                maxLength={500}
              />
              <TouchableOpacity
                style={[
                  styles.sendButton,
                  newMessage.trim() === '' && styles.sendButtonDisabled
                ]}
                onPress={handleSendMessage}
                disabled={newMessage.trim() === ''}
              >
                <Ionicons name="send" size={20} color="#ffffff" />
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Support Ticket Modal */}
      <Modal
        visible={showTicketModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowTicketModal(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.ticketModalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Submit Support Ticket</Text>
              <TouchableOpacity onPress={() => setShowTicketModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.ticketForm} showsVerticalScrollIndicator={false}>
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Subject *</Text>
                <TextInput
                  style={styles.textInput}
                  value={ticketForm.subject}
                  onChangeText={(text) => setTicketForm({...ticketForm, subject: text})}
                  placeholder="Brief description of the issue"
                  placeholderTextColor="#94A3B8"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Email *</Text>
                <TextInput
                  style={styles.textInput}
                  value={ticketForm.email}
                  onChangeText={(text) => setTicketForm({...ticketForm, email: text})}
                  placeholder="your.email@example.com"
                  placeholderTextColor="#94A3B8"
                  keyboardType="email-address"
                  autoCapitalize="none"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Description *</Text>
                <TextInput
                  style={[styles.textInput, styles.textArea]}
                  value={ticketForm.description}
                  onChangeText={(text) => setTicketForm({...ticketForm, description: text})}
                  placeholder="Please provide detailed information about the issue..."
                  placeholderTextColor="#94A3B8"
                  multiline
                  numberOfLines={5}
                  textAlignVertical="top"
                />
              </View>

              <TouchableOpacity
                style={[
                  styles.submitButton,
                  ticketSubmitting && styles.submitButtonDisabled
                ]}
                onPress={handleSubmitTicket}
                disabled={ticketSubmitting}
              >
                {ticketSubmitting ? (
                  <ActivityIndicator size={20} color="#ffffff" />
                ) : (
                  <MaterialIcons name="support-agent" size={20} color="#ffffff" />
                )}
                <Text style={styles.submitButtonText}>
                  {ticketSubmitting ? 'Submitting...' : 'Submit Ticket'}
                </Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* System Status Modal */}
      <Modal
        visible={showSystemStatusModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowSystemStatusModal(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.statusModalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>System Status</Text>
              <TouchableOpacity onPress={() => setShowSystemStatusModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.statusContent} showsVerticalScrollIndicator={false}>
              <View style={styles.overallStatus}>
                <View style={[
                  styles.statusIndicator,
                  { backgroundColor: systemStatus.overall === 'operational' ? '#10B981' : '#EF4444' }
                ]} />
                <Text style={styles.overallStatusText}>
                  All Systems {systemStatus.overall === 'operational' ? 'Operational' : 'Issues Detected'}
                </Text>
              </View>

              <Text style={styles.servicesTitle}>Service Status</Text>
              {systemStatus.services.map((service, index) => (
                <View key={index} style={styles.serviceItem}>
                  <View style={styles.serviceInfo}>
                    <Text style={styles.serviceName}>{service.name}</Text>
                    <Text style={styles.serviceUptime}>Uptime: {service.uptime}</Text>
                  </View>
                  <View style={[
                    styles.serviceStatus,
                    { 
                      backgroundColor: service.status === 'operational' ? '#10B981' : 
                                     service.status === 'maintenance' ? '#F59E0B' : '#EF4444'
                    }
                  ]}>
                    <Text style={styles.serviceStatusText}>
                      {service.status.charAt(0).toUpperCase() + service.status.slice(1)}
                    </Text>
                  </View>
                </View>
              ))}

              <Text style={styles.lastUpdated}>
                Last updated: {systemStatus.lastUpdated}
              </Text>
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Feedback Modal */}
      <Modal
        visible={showFeedbackModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowFeedbackModal(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.feedbackModalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Share Your Feedback</Text>
              <TouchableOpacity onPress={() => setShowFeedbackModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.feedbackForm} showsVerticalScrollIndicator={false}>
              <View style={styles.ratingContainer}>
                <Text style={styles.inputLabel}>How would you rate your experience? *</Text>
                <View style={styles.starsContainer}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <TouchableOpacity
                      key={star}
                      onPress={() => setFeedbackForm({...feedbackForm, rating: star})}
                    >
                      <Ionicons
                        name={star <= feedbackForm.rating ? 'star' : 'star-outline'}
                        size={32}
                        color={star <= feedbackForm.rating ? '#F59E0B' : '#D1D5DB'}
                      />
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Feedback *</Text>
                <TextInput
                  style={[styles.textInput, styles.textArea]}
                  value={feedbackForm.feedback}
                  onChangeText={(text) => setFeedbackForm({...feedbackForm, feedback: text})}
                  placeholder="Tell us about your experience..."
                  placeholderTextColor="#94A3B8"
                  multiline
                  numberOfLines={4}
                  textAlignVertical="top"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Suggestions</Text>
                <TextInput
                  style={[styles.textInput, styles.textArea]}
                  value={feedbackForm.suggestions}
                  onChangeText={(text) => setFeedbackForm({...feedbackForm, suggestions: text})}
                  placeholder="Any suggestions for improvement?"
                  placeholderTextColor="#94A3B8"
                  multiline
                  numberOfLines={3}
                  textAlignVertical="top"
                />
              </View>

              <TouchableOpacity
                style={styles.submitButton}
                onPress={handleSubmitFeedback}
              >
                <MaterialIcons name="feedback" size={20} color="#ffffff" />
                <Text style={styles.submitButtonText}>Submit Feedback</Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  header: {
    backgroundColor: '#0e4e96',
    paddingVertical: 15,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    color: '#ffffff',
    fontSize: 18,
    fontWeight: '600',
    flex: 1,
    textAlign: 'center',
    marginHorizontal: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#64748B',
    textAlign: 'center',
  },
  tabContainer: {
    flexDirection: 'row',
    backgroundColor: '#ffffff',
    paddingHorizontal: 16,
    paddingVertical: 8,
    gap: 8,
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: '#F1F5F9',
    borderRadius: 12,
    gap: 6,
  },
  activeTab: {
    backgroundColor: '#0e4e96',
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
  },
  activeTabText: {
    color: '#ffffff',
  },
  container: {
    flex: 1,
  },
  tabContent: {
    flex: 1,
    padding: 16,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1E293B',
    marginBottom: 8,
  },
  sectionDescription: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 20,
    lineHeight: 20,
  },
  quickActionsContainer: {
    marginBottom: 24,
  },
  quickActionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  quickActionCard: {
    flex: 1,
    minWidth: (width - 48) / 2 - 6,
    backgroundColor: '#ffffff',
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  quickActionIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  quickActionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 4,
    textAlign: 'center',
  },
  quickActionDescription: {
    fontSize: 12,
    color: '#64748B',
    textAlign: 'center',
  },
  searchContainer: {
    marginBottom: 24,
  },
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: '#1E293B',
  },
  faqsContainer: {
    gap: 12,
  },
  faqCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  faqHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
  },
  faqTitleContainer: {
    flex: 1,
  },
  faqCategory: {
    fontSize: 12,
    fontWeight: '500',
    color: '#0e4e96',
    marginBottom: 4,
  },
  faqQuestion: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1E293B',
    lineHeight: 20,
  },
  faqContent: {
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  faqAnswer: {
    fontSize: 14,
    color: '#374151',
    lineHeight: 20,
    marginBottom: 12,
  },
  faqNavigationButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#F0F8FF',
    borderRadius: 8,
    alignSelf: 'flex-start',
    gap: 6,
  },
  faqNavigationText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#0e4e96',
  },
  navigationContainer: {
    flex: 1,
  },
  navigationGrid: {
    gap: 12,
  },
  navigationCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    padding: 16,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  navigationIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16,
  },
  navigationTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 2,
    flex: 1,
  },
  navigationDescription: {
    fontSize: 12,
    color: '#64748B',
    flex: 1,
  },
  navigationArrow: {
    marginLeft: 8,
  },
  contactInfoContainer: {
    marginBottom: 24,
  },
  contactMethodsGrid: {
    gap: 16,
  },
  contactMethod: {
    backgroundColor: '#ffffff',
    padding: 20,
    borderRadius: 12,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  contactIcon: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  contactMethodTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 4,
  },
  contactMethodText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#0e4e96',
    marginBottom: 4,
  },
  contactMethodSubtext: {
    fontSize: 12,
    color: '#64748B',
    textAlign: 'center',
  },
  contactFormContainer: {
    backgroundColor: '#ffffff',
    padding: 20,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  formTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 20,
  },
  inputGroup: {
    marginBottom: 16,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 6,
  },
  textInput: {
    backgroundColor: '#F8FAFC',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 14,
    color: '#1E293B',
  },
  textArea: {
    height: 100,
    textAlignVertical: 'top',
  },
  submitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#0e4e96',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 8,
    gap: 8,
    marginTop: 8,
  },
  submitButtonDisabled: {
    backgroundColor: '#94A3B8',
  },
  submitButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
  },
  modalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  chatModalContent: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    height: '80%',
    paddingTop: 20,
  },
  chatHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  chatHeaderInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  aiAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#0e4e96',
    alignItems: 'center',
    justifyContent: 'center',
  },
  chatHeaderTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1E293B',
  },
  chatHeaderSubtitle: {
    fontSize: 12,
    color: '#64748B',
  },
  chatMessages: {
    flex: 1,
    paddingHorizontal: 20,
    paddingTop: 16,
  },
  messageContainer: {
    marginBottom: 16,
  },
  messageBubble: {
    maxWidth: '80%',
    padding: 12,
    borderRadius: 16,
    marginBottom: 8,
  },
  userMessage: {
    backgroundColor: '#0e4e96',
    alignSelf: 'flex-end',
    borderBottomRightRadius: 4,
  },
  botMessage: {
    backgroundColor: '#F1F5F9',
    alignSelf: 'flex-start',
    borderBottomLeftRadius: 4,
  },
  messageText: {
    fontSize: 14,
    lineHeight: 20,
  },
  userMessageText: {
    color: '#ffffff',
  },
  botMessageText: {
    color: '#1E293B',
  },
  suggestionsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  suggestionButton: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 16,
  },
  suggestionText: {
    fontSize: 12,
    color: '#0e4e96',
    fontWeight: '500',
  },
  typingIndicator: {
    alignSelf: 'flex-start',
    marginBottom: 16,
  },
  typingBubble: {
    backgroundColor: '#F1F5F9',
    padding: 12,
    borderRadius: 16,
    borderBottomLeftRadius: 4,
  },
  typingText: {
    fontSize: 14,
    color: '#64748B',
    fontStyle: 'italic',
  },
  chatInputContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: '#F1F5F9',
    gap: 12,
  },
  chatInput: {
    flex: 1,
    backgroundColor: '#F8FAFC',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 14,
    color: '#1E293B',
    maxHeight: 100,
  },
  sendButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#0e4e96',
    alignItems: 'center',
    justifyContent: 'center',
  },
  sendButtonDisabled: {
    backgroundColor: '#94A3B8',
  },
  ticketModalContent: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    height: '70%',
    paddingTop: 20,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1E293B',
  },
  ticketForm: {
    flex: 1,
    paddingHorizontal: 20,
    paddingTop: 20,
  },
  statusModalContent: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    height: '60%',
    paddingTop: 20,
  },
  statusContent: {
    flex: 1,
    paddingHorizontal: 20,
    paddingTop: 20,
  },
  overallStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
    gap: 12,
  },
  statusIndicator: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  overallStatusText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1E293B',
  },
  servicesTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 16,
  },
  serviceItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  serviceInfo: {
    flex: 1,
  },
  serviceName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#1E293B',
    marginBottom: 2,
  },
  serviceUptime: {
    fontSize: 12,
    color: '#64748B',
  },
  serviceStatus: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 12,
  },
  serviceStatusText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#ffffff',
  },
  lastUpdated: {
    fontSize: 12,
    color: '#64748B',
    textAlign: 'center',
    marginTop: 20,
    fontStyle: 'italic',
  },
  feedbackModalContent: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    height: '70%',
    paddingTop: 20,
  },
  feedbackForm: {
    flex: 1,
    paddingHorizontal: 20,
    paddingTop: 20,
  },
  ratingContainer: {
    marginBottom: 20,
  },
  starsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
    marginTop: 12,
  },
});
